/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

import "./index.css";

import { $generateHtmlFromNodes } from "@lexical/html";
import {
    $createMarkNode,
    $getMarkIDs,
    $isMarkNode,
    $unwrapMarkNode,
    $wrapSelectionInMarkNode,
    MarkNode,
} from "@lexical/mark";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { useCollaborationContext } from "@lexical/react/LexicalCollaborationContext";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { createDOMRange, createRectsFromDOMRange } from "@lexical/selection";
import { $isRootTextContentEmpty, $rootTextContent } from "@lexical/text";
import { $dfs, mergeRegister, registerNestedElementResolver } from "@lexical/utils";
import type { Provider } from "@lexical/yjs";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Tooltip } from "@mui/material";
import ActionSend from "assets/img/svg/actionSend";
import classNames from "classnames";
import DeleteModal from "components/shared/DeleteModal";
import StyledButton from "components/shared/StyledButton";
import { useAgreementPageContext } from "containers/ContractValidationEdit/context/StateProvider";
import { Body, BodyHighlight, SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import type { EditorState, LexicalCommand, LexicalEditor, NodeKey, RangeSelection } from "lexical";
import {
    $getNodeByKey,
    $getRoot,
    $getSelection,
    $isParagraphNode,
    $isRangeSelection,
    $isTextNode,
    CLEAR_EDITOR_COMMAND,
    COMMAND_PRIORITY_EDITOR,
    createCommand,
    KEY_ESCAPE_COMMAND,
} from "lexical";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AgreementStatus, deleteAgreementsComment, postAgreementsComment, putAgreement } from "store/agreementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { Doc } from "yjs";

import Button from "../Button";
import ContentEditable from "../ContentEditable";
import Placeholder from "../Placeholder";
import i18n from "./../../../../i18n";
import CommentEditorTheme from "./CommentEditorTheme";
import type { Comment, Comments, Thread } from "./commenting";
import { CommentStore, createComment, createThread, triggerOnChange, useCommentStore } from "./commenting";
import { useModal } from "./useModal";

export const INSERT_INLINE_COMMAND: LexicalCommand<void> = createCommand("INSERT_INLINE_COMMAND");

function AddCommentBox({
    anchorKey,
    editor,
    onAddComment,
}: {
    anchorKey: NodeKey;
    editor: LexicalEditor;
    onAddComment: () => void;
}): JSX.Element {
    const boxRef = useRef<HTMLDivElement>(null);

    const updatePosition = useCallback(() => {
        const boxElem = boxRef.current;
        const rootElement = editor.getRootElement();
        const anchorElement = editor.getElementByKey(anchorKey);

        if (boxElem !== null && rootElement !== null && anchorElement !== null) {
            const { right } = rootElement.getBoundingClientRect();
            const { top } = anchorElement.getBoundingClientRect();
            boxElem.style.left = `${right - 20}px`;
            boxElem.style.top = `${top - 30}px`;
        }
    }, [anchorKey, editor]);

    useEffect(() => {
        window.addEventListener("resize", updatePosition);

        return () => {
            window.removeEventListener("resize", updatePosition);
        };
    }, [editor, updatePosition]);

    useLayoutEffect(() => {
        updatePosition();
    }, [anchorKey, editor, updatePosition]);

    return (
        <div className="CommentPlugin_AddCommentBox" ref={boxRef}>
            <button className="CommentPlugin_AddCommentBox_button" onClick={onAddComment}>
                <i className="icon add-comment" />
            </button>
        </div>
    );
}

function EscapeHandlerPlugin({ onEscape }: { onEscape: (e: KeyboardEvent) => boolean }): null {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand(
            KEY_ESCAPE_COMMAND,
            (event: KeyboardEvent) => {
                return onEscape(event);
            },
            2,
        );
    }, [editor, onEscape]);

    return null;
}

function PlainTextEditor({
    className,
    autoFocus,
    onEscape,
    onChange,
    editorRef,
    placeholder = "Type a comment...",
}: {
    autoFocus?: boolean;
    className?: string;
    editorRef?: { current: null | LexicalEditor };
    onChange: (editorState: EditorState, editor: LexicalEditor) => void;
    onEscape: (e: KeyboardEvent) => boolean;
    placeholder?: string;
}) {
    const initialConfig = {
        namespace: "Commenting",
        nodes: [],
        onError: (error: Error) => {
            throw error;
        },
        theme: CommentEditorTheme,
    };

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <div className="CommentPlugin_CommentInputBox_EditorContainer">
                <PlainTextPlugin
                    contentEditable={<ContentEditable className={className} />}
                    placeholder={<Placeholder>{placeholder}</Placeholder>}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <OnChangePlugin onChange={onChange} />
                <HistoryPlugin />
                {autoFocus !== false && <AutoFocusPlugin />}
                <EscapeHandlerPlugin onEscape={onEscape} />
                <ClearEditorPlugin />
                {editorRef !== undefined && <EditorRefPlugin editorRef={editorRef} />}
            </div>
        </LexicalComposer>
    );
}

function useOnChange(setContent: (text: string) => void, setCanSubmit: (canSubmit: boolean) => void) {
    return useCallback(
        (editorState: EditorState, _editor: LexicalEditor) => {
            editorState.read(() => {
                setContent($rootTextContent());
                setCanSubmit(!$isRootTextContentEmpty(_editor.isComposing(), true));
            });
        },
        [setCanSubmit, setContent],
    );
}

function CommentInputBox({
    editor,
    cancelAddComment,
    submitAddComment,
}: {
    cancelAddComment: () => void;
    editor: LexicalEditor;
    submitAddComment: (
        commentOrThread: Comment | Thread,
        isInlineComment: boolean,
        thread?: Thread,
        selection?: RangeSelection | null,
    ) => void;
}) {
    const [t] = useTranslation();
    const [content, setContent] = useState("");
    const [canSubmit, setCanSubmit] = useState(false);
    const boxRef = useRef<HTMLDivElement>(null);
    const selectionState = useMemo(
        () => ({
            container: document.createElement("div"),
            elements: [],
        }),
        [],
    );
    const selectionRef = useRef<RangeSelection | null>(null);
    const author = t("ContractValidationEdit.You");

    const updateLocation = useCallback(() => {
        editor.getEditorState().read(() => {
            const selection = $getSelection();

            if ($isRangeSelection(selection)) {
                selectionRef.current = selection.clone();
                const anchor = selection.anchor;
                const focus = selection.focus;
                const range = createDOMRange(editor, anchor.getNode(), anchor.offset, focus.getNode(), focus.offset);
                const boxElem = boxRef.current;
                if (range !== null && boxElem !== null) {
                    const { left, bottom, width } = range.getBoundingClientRect();
                    const selectionRects = createRectsFromDOMRange(editor, range);
                    let correctedLeft = selectionRects.length === 1 ? left + width / 2 - 200 : left - 200;
                    if (correctedLeft < 10) {
                        correctedLeft = 10;
                    }
                    boxElem.style.left = `${correctedLeft}px`;
                    boxElem.style.top = `${bottom + 20 + (window.pageYOffset || document.documentElement.scrollTop)}px`;
                    const selectionRectsLength = selectionRects.length;
                    const { container } = selectionState;
                    const elements: Array<HTMLSpanElement> = selectionState.elements;
                    const elementsLength = elements.length;

                    for (let i = 0; i < selectionRectsLength; i++) {
                        const selectionRect = selectionRects[i];
                        let elem: HTMLSpanElement = elements[i];
                        if (elem === undefined) {
                            elem = document.createElement("span");
                            elements[i] = elem;
                            container.appendChild(elem);
                        }
                        const color = "255, 212, 0";
                        const style = `position:absolute;top:${
                            selectionRect.top + (window.pageYOffset || document.documentElement.scrollTop)
                        }px;left:${selectionRect.left}px;height:${selectionRect.height}px;width:${
                            selectionRect.width
                        }px;background-color:rgba(${color}, 0.3);pointer-events:none;z-index:5;`;
                        elem.style.cssText = style;
                    }
                    for (let i = elementsLength - 1; i >= selectionRectsLength; i--) {
                        const elem = elements[i];
                        container.removeChild(elem);
                        elements.pop();
                    }
                }
            }
        });
    }, [editor, selectionState]);

    useLayoutEffect(() => {
        updateLocation();
        const container = selectionState.container;
        const body = document.body;
        if (body !== null) {
            body.appendChild(container);
            return () => {
                body.removeChild(container);
            };
        }
    }, [selectionState.container, updateLocation]);

    useEffect(() => {
        window.addEventListener("resize", updateLocation);

        return () => {
            window.removeEventListener("resize", updateLocation);
        };
    }, [updateLocation]);

    const onEscape = (event: KeyboardEvent): boolean => {
        event.preventDefault();
        cancelAddComment();
        return true;
    };

    const submitComment = () => {
        if (canSubmit) {
            let quote = editor.getEditorState().read(() => {
                const selection = selectionRef.current;
                return selection ? selection.getTextContent() : "";
            });
            if (quote.length > 100) {
                quote = quote.slice(0, 99) + "…";
            }
            submitAddComment(
                createThread(quote, [createComment(content, author)]),
                true,
                undefined,
                selectionRef.current,
            );
            selectionRef.current = null;
        }
    };

    const onChange = useOnChange(setContent, setCanSubmit);

    return (
        <div className="CommentPlugin_CommentInputBox" ref={boxRef}>
            <PlainTextEditor
                className="CommentPlugin_CommentInputBox_Editor"
                onEscape={onEscape}
                placeholder={t("ContractValidationEdit.WriteAComment")}
                onChange={onChange}
            />
            <div className="CommentPlugin_CommentInputBox_Buttons">
                {/* <Button onClick={cancelAddComment} className="CommentPlugin_CommentInputBox_Button">
                    Cancel
                </Button> */}
                <StyledButton
                    buttonType="outlined"
                    onClick={submitComment}
                    disabled={!canSubmit}
                    className="CommentPlugin_CommentInputBox_Button primary"
                >
                    <div style={{ width: "18px", height: "18px" }}>
                        <ActionSend />
                    </div>
                    {t("ContractValidationEdit.Send")}
                </StyledButton>
            </div>
        </div>
    );
}

function CommentsComposer({
    submitAddComment,
    thread,
    placeholder,
}: {
    placeholder?: string;
    submitAddComment: (
        commentOrThread: Comment,
        isInlineComment: boolean,
        // eslint-disable-next-line no-shadow
        thread?: Thread,
    ) => void;
    thread?: Thread;
}) {
    const [t] = useTranslation();
    const [content, setContent] = useState("");
    const [canSubmit, setCanSubmit] = useState(false);
    const { agreement } = useAppSelector((state) => state.agreements);
    const editorRef = useRef<LexicalEditor>(null);
    const author = t("ContractValidationEdit.You");

    const onChange = useOnChange(setContent, setCanSubmit);

    const submitComment = () => {
        if (canSubmit) {
            submitAddComment(createComment(content, author), false, thread);
            const editor = editorRef.current;
            if (editor !== null) {
                editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
            }
        }
    };

    return (
        <>
            <PlainTextEditor
                className="CommentPlugin_CommentsPanel_Editor"
                autoFocus={false}
                onEscape={() => {
                    return true;
                }}
                onChange={onChange}
                editorRef={editorRef}
                placeholder={placeholder}
            />
            <StyledButton
                buttonType="secondary"
                className="CommentPlugin_CommentsPanel_SendButton"
                onClick={submitComment}
                disabled={!canSubmit || agreement?.status == AgreementStatus.SENT}
            >
                <ActionSend style={{ height: "16px", width: "16px" }} />
            </StyledButton>
        </>
    );
}

function CommentsPanelListComment({
    comment,
    deleteComment,
    thread,
    rtf,
}: {
    comment: Comment;
    deleteComment: (commentOrThread: Comment) => void;
    rtf: Intl.RelativeTimeFormat;
    thread?: Thread;
}): JSX.Element {
    const seconds = Math.round((comment.timeStamp - new Date().getTime()) / 1000);
    const minutes = Math.round(seconds / 60);
    const hours = Math.ceil(minutes / 60);
    const days = Math.ceil(hours / 24);
    const dispatch = useAppDispatch();
    const { userData } = useAppSelector((state) => state.settings);
    const { id } = useParams();
    const isParentThreadComment =
        thread?.comments && thread.comments.length > 1 && thread?.comments[0].backendId === comment.backendId;
    const isMainComment = thread?.comments[0].backendId === comment.backendId;

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [t] = useTranslation();
    const time = days < 0 ? days : hours < 0 ? hours : minutes;
    const timeFormat = days < 0 ? "days" : hours < 0 ? "hours" : "minutes";

    const isCommentOwner = userData?.id === comment.userId;

    const handleOpenDeleteModal = (e: any) => {
        e.stopPropagation();
        setShowDeleteModal(true);
    };

    const handleDeleteComment = async () => {
        if (!comment.backendId) return;
        if (isMainComment) {
            deleteComment(comment); // Delete comment yellow mark on the editor
        }
        dispatch(deleteAgreementsComment({ agreementId: +id!, commentId: comment.backendId }));
    };

    return (
        <li className="CommentPlugin_CommentsPanel_List_Comment">
            <div className="CommentPlugin_CommentsPanel_List_Details">
                <span className="CommentPlugin_CommentsPanel_List_Comment_Author">{comment.author}</span>
                <span className="CommentPlugin_CommentsPanel_List_Comment_Time">
                    {seconds > -10 ? t("ContractValidationEdit.JustNow") : rtf.format(time, timeFormat)}
                    {/* {rtf.format(time, minutes < -60 ? "hour" : "minute")} */}
                </span>
            </div>
            <p className={comment.deleted ? "CommentPlugin_CommentsPanel_DeletedComment" : ""}>{comment.content}</p>
            {!comment.deleted && isCommentOwner && (
                <>
                    <DeleteOutlineOutlinedIcon
                        className="CommentPlugin_CommentsPanel_List_DeleteButton"
                        onClick={handleOpenDeleteModal}
                    />
                    <DeleteModal
                        elementTitle={
                            isParentThreadComment
                                ? t("ContractValidationEdit.TheThread")
                                : t("ContractValidationEdit.TheComment")
                        }
                        description={
                            isParentThreadComment
                                ? t("ContractValidationEdit.YouWillDeleteThread", { commentContent: comment.content })
                                : `”${comment.content}“`
                        }
                        open={showDeleteModal && isCommentOwner}
                        onClose={() => setShowDeleteModal(false)}
                        onDelete={handleDeleteComment}
                    />
                </>
            )}
        </li>
    );
}

function CommentsPanelList({
    activeIDs,
    comments,
    deleteCommentOrThread,
    listRef,
    submitAddComment,
    markNodeMap,
}: {
    activeIDs: Array<string>;
    comments: Comments;
    deleteCommentOrThread: (commentOrThread: Comment) => void;
    listRef: { current: null | HTMLUListElement };
    markNodeMap: Map<string, Set<NodeKey>>;
    submitAddComment: (commentOrThread: Comment | Thread, isInlineComment: boolean, thread?: Thread) => void;
}): JSX.Element {
    const [editor] = useLexicalComposerContext();
    const [t] = useTranslation();
    const [counter, setCounter] = useState(0);
    const [modal, showModal] = useModal();
    const rtf = useMemo(
        () =>
            new Intl.RelativeTimeFormat(i18n.language ?? "fr", {
                localeMatcher: "best fit",
                numeric: "auto",
                style: "short",
            }),
        [],
    );

    useEffect(() => {
        // Used to keep the time stamp up to date
        const id = setTimeout(() => {
            setCounter(counter + 1);
        }, 10000);

        return () => {
            clearTimeout(id);
        };
    }, [counter]);

    return (
        <ul className="CommentPlugin_CommentsPanel_List" ref={listRef}>
            {comments.map((commentOrThread) => {
                const id = commentOrThread.id;
                if (commentOrThread.type === "thread") {
                    const handleClickThread = () => {
                        const markNodeKeys = markNodeMap.get(id);
                        if (markNodeKeys !== undefined && (activeIDs === null || activeIDs.indexOf(id) === -1)) {
                            const activeElement = document.activeElement;
                            // Move selection to the start of the mark, so that we
                            // update the UI with the selected thread.
                            editor.update(
                                () => {
                                    const markNodeKey = Array.from(markNodeKeys)[0];
                                    const markNode = $getNodeByKey<MarkNode>(markNodeKey);
                                    const markElement = editor.getElementByKey(markNodeKey);
                                    markElement?.scrollIntoView({ behavior: "smooth", block: "center" });
                                    if ($isMarkNode(markNode)) {
                                        markNode.selectStart();
                                    }
                                },
                                {
                                    onUpdate() {
                                        // Restore selection to the previous element
                                        if (activeElement !== null) {
                                            (activeElement as HTMLElement).focus();
                                        }
                                    },
                                },
                            );
                        }
                    };

                    return (
                        <li
                            key={id}
                            onClick={handleClickThread}
                            className={classNames({
                                CommentPlugin_CommentsPanel_List_Thread: true,
                                interactive: markNodeMap.has(id),
                                active: activeIDs.includes(id),
                                [`selected-comment-${id}`]: activeIDs.includes(id),
                            })}
                        >
                            {/* <div className="CommentPlugin_CommentsPanel_List_Thread_QuoteBox"> */}
                            {/* <blockquote className="CommentPlugin_CommentsPanel_List_Thread_Quote">
                                    {"> "}
                                    <span>{commentOrThread.quote}</span>
                                </blockquote> */}
                            {/* INTRODUCE DELETE THREAD HERE*/}
                            {/* We cannot delete any comment */}
                            {/* <Button
                                    onClick={() => {
                                        showModal("Delete Thread", (onClose) => (
                                            <ShowDeleteCommentOrThreadDialog
                                                commentOrThread={commentOrThread}
                                                deleteCommentOrThread={deleteCommentOrThread}
                                                onClose={onClose}
                                            />
                                        ));
                                    }}
                                    className="CommentPlugin_CommentsPanel_List_DeleteButton"
                                >
                                    <i className="delete" />
                                </Button> */}
                            {modal}
                            {/* </div> */}
                            <ul className="CommentPlugin_CommentsPanel_List_Thread_Comments">
                                {commentOrThread.comments.map((comment) => (
                                    <CommentsPanelListComment
                                        key={comment.id}
                                        comment={comment}
                                        deleteComment={deleteCommentOrThread}
                                        thread={commentOrThread}
                                        rtf={rtf}
                                    />
                                ))}
                            </ul>
                            <div className="CommentPlugin_CommentsPanel_List_Thread_Editor">
                                <CommentsComposer
                                    submitAddComment={submitAddComment}
                                    thread={commentOrThread}
                                    placeholder={t("ContractValidationEdit.WriteAReply")}
                                />
                            </div>
                        </li>
                    );
                }
                return (
                    <CommentsPanelListComment
                        key={id}
                        comment={commentOrThread}
                        deleteComment={deleteCommentOrThread}
                        rtf={rtf}
                    />
                );
            })}
        </ul>
    );
}

function CommentsPanel({
    activeIDs,
    deleteCommentOrThread,
    comments,
    submitAddComment,
    markNodeMap,
}: {
    activeIDs: Array<string>;
    comments: Comments;
    deleteCommentOrThread: (commentOrThread: Comment) => void;
    markNodeMap: Map<string, Set<NodeKey>>;
    submitAddComment: (commentOrThread: Comment | Thread, isInlineComment: boolean, thread?: Thread) => void;
}): JSX.Element {
    const [t] = useTranslation();
    const listRef = useRef<HTMLUListElement>(null);
    const isEmpty = comments.length === 0;

    return (
        <div className="CommentPlugin_CommentsPanel">
            <BodyHighlight color="black">
                {t("ContractValidationEdit.Comments")} ({comments?.length})
            </BodyHighlight>
            <SmallBody color="grey">{t("ContractValidationEdit.HighlightTextToComment")}</SmallBody>
            {isEmpty ? (
                <div className="CommentPlugin_CommentsPanel_Empty">
                    <SmallBody color="grey" style={{ fontSize: "11px" }}>
                        {t("ContractValidationEdit.NoCommentsYet")}
                    </SmallBody>
                </div>
            ) : (
                <CommentsPanelList
                    activeIDs={activeIDs}
                    comments={comments}
                    deleteCommentOrThread={deleteCommentOrThread}
                    listRef={listRef}
                    submitAddComment={submitAddComment}
                    markNodeMap={markNodeMap}
                />
            )}
        </div>
    );
}

function useCollabAuthorName(): string {
    const collabContext = useCollaborationContext();
    const { yjsDocMap, name } = collabContext;
    return yjsDocMap.has("comments") ? name : "Playground User";
}

export default function CommentPlugin({
    providerFactory,
}: {
    providerFactory?: (id: string, yjsDocMap: Map<string, Doc>) => Provider;
}): JSX.Element {
    const collabContext = useCollaborationContext();
    const [editor] = useLexicalComposerContext();
    const markNodeMap = useMemo<Map<string, Set<NodeKey>>>(() => {
        return new Map();
    }, []);
    const [activeAnchorKey, setActiveAnchorKey] = useState<NodeKey | null>();
    const [activeIDs, setActiveIDs] = useState<Array<string>>([]);
    const [showCommentInput, setShowCommentInput] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [activeCommentClassName, setActiveCommentClassName] = useState("");
    const { rightPanelRef } = useAgreementPageContext();
    const { userData } = useAppSelector((state) => state.settings);

    const { yjsDocMap } = collabContext;
    const dispatch = useAppDispatch();
    const { agreement, agreementsComments } = useAppSelector((state) => state.agreements);
    const { data } = useAppSelector((state) => state.adminPanel.users.company);
    const commentsWithAuthors = useMemo(() => {
        if (agreementsComments == null) return [];
        return agreementsComments.map((comment) => {
            const newComment = {
                ...comment,
                author: data.find((user) => +user.id === comment?.userId)?.firstname ?? "Unknown",
            };
            return {
                ...newComment,
                thread: newComment?.thread?.map((threadComment) => {
                    const author = data.find((user) => +user.id === threadComment.userId)?.firstname;
                    return { ...threadComment, author: author ?? "Unknown" };
                }),
            };
        });
    }, [data, agreementsComments]);
    const commentStore = useMemo(() => new CommentStore(editor, commentsWithAuthors), [editor, commentsWithAuthors]);
    const comments = useCommentStore(commentStore, commentsWithAuthors);
    const commentListElement = document.querySelector(".contract-validation-edit-right-panel-comments") as HTMLElement;

    const handleOnScroll = () => setShowCommentInput(false);

    useEffect(() => {
        const editorElement: HTMLDivElement | null = document.querySelector(".ContentEditable__root");
        if (editorElement == null) return;
        editorElement.addEventListener("scroll", handleOnScroll);

        return () => {
            editorElement.removeEventListener("scroll", handleOnScroll);
        };
    }, [showCommentInput]);

    useEffect(() => {
        if (providerFactory) {
            const provider = providerFactory("comments", yjsDocMap);
            return commentStore.registerCollaboration(provider);
        }
    }, [commentStore, providerFactory, yjsDocMap]);

    const cancelAddComment = useCallback(() => {
        editor.update(() => {
            const selection = $getSelection();
            // Restore selection
            if (selection !== null) {
                selection.dirty = true;
            }
        });
        setShowCommentInput(false);
    }, [editor]);

    /**
     * This function remove the yellow mark when delete a comment on the rich-text-editor
     */
    const deleteCommentOrThread = useCallback(
        (comment: Comment) => {
            // Remove ids from associated marks
            const id = comment.threadId as string;
            const markNodeKeys = markNodeMap.get(id);
            if (markNodeKeys !== undefined) {
                // Do async to avoid causing a React infinite loop
                editor.update(
                    () => {
                        for (const key of markNodeKeys) {
                            const node: null | MarkNode = $getNodeByKey(key);
                            if ($isMarkNode(node)) {
                                node.deleteID(id);
                                if (node.getIDs().length === 0) {
                                    $unwrapMarkNode(node);
                                }
                            }
                        }
                    },
                    {
                        onUpdate: () => {
                            editor.update(() => {
                                const editorState = editor.getEditorState();
                                const jsonString = JSON.stringify(editorState);
                                const data = { blocks: jsonString };
                                dispatch(putAgreement({ data, id: comment.agreementId! }));
                            });
                        },
                    },
                );
            }
        },
        [commentStore, editor, markNodeMap],
    );

    const postCommentToBackend = (
        commentOrThread: Comment | Thread,
        isInlineComment: boolean,
        thread?: Thread,
        selection?: RangeSelection | null,
    ) => {
        editor.update(() => {
            const editorState = editor.getEditorState();
            const jsonString = JSON.stringify(editorState);
            const htmlString = $generateHtmlFromNodes(editor, null);
            const newTemplate = {
                blocks: jsonString,
                // html: htmlString,
            };
            // console.log(commentOrThread)
            // a thread that contains a comment
            // {
            //     "comments": [
            //       {
            //         "author": "Vous",
            //         "content": "egegs",
            //         "deleted": false,
            //         "id": "xuion",
            //         "timeStamp": 626927,
            //         "type": "comment"
            //       }
            //     ],
            //     "id": "qsacs",
            //     "quote": "O DE L'ED",
            //     "type": "thread"
            //   }
            const comment = {
                uuid: commentOrThread.id,
                ...(commentOrThread.type == "comment" ? { parentId: thread?.backendId } : {}),
                content:
                    commentOrThread.type == "comment" ? commentOrThread.content : commentOrThread.comments[0].content,
            };

            dispatch(putAgreement({ data: newTemplate, id: agreement?.id ?? -1 }));
            dispatch(postAgreementsComment({ data: comment, id: agreement?.id ?? -1 })).then((res) => {
                const { id: backendId, agreementId } = res.payload.data;
                commentStore.addComment(
                    {
                        ...commentOrThread,
                        ...(commentOrThread.type == "thread"
                            ? {
                                  backendId,
                                  agreementId,
                                  userId: userData?.id,
                                  comments: commentOrThread.comments.map((item) => ({
                                      ...item,
                                      userId: userData?.id,
                                      backendId,
                                      agreementId,
                                  })),
                              }
                            : {}),
                        userId: userData?.id,
                        agreementId,
                        backendId,
                    },
                    thread,
                );
            });
        });
    };

    // a thread is when we add a new comment from a selection
    // a comment is when we reply to a thread
    const submitAddComment = useCallback(
        (
            commentOrThread: Comment | Thread,
            isInlineComment: boolean, // true when it's a new thread
            thread?: Thread,
            selection?: RangeSelection | null,
        ) => {
            // Handle scroll to new created comment
            if (!thread) {
                setActiveIDs([commentOrThread.id]);
                setActiveCommentClassName(`.selected-comment-${commentOrThread.id}`);
            }
            if (isInlineComment) {
                editor.update(
                    () => {
                        if ($isRangeSelection(selection)) {
                            const isBackward = selection.isBackward();
                            const id = commentOrThread.id;
                            // Wrap content in a MarkNode
                            $wrapSelectionInMarkNode(selection, isBackward, id);
                        }
                    },
                    { onUpdate: () => postCommentToBackend(commentOrThread, isInlineComment, thread, selection) },
                );
                setShowCommentInput(false);
            } else {
                postCommentToBackend(commentOrThread, isInlineComment, thread, selection);
            }
        },
        [commentStore, editor],
    );

    useEffect(() => {
        const changedElems: Array<HTMLElement> = [];
        for (let i = 0; i < activeIDs.length; i++) {
            const id = activeIDs[i];
            const keys = markNodeMap.get(id);
            if (keys !== undefined) {
                for (const key of keys) {
                    const elem = editor.getElementByKey(key);
                    if (elem !== null) {
                        elem.classList.add("selected");
                        changedElems.push(elem);
                        setShowComments(true);
                    }
                }
            }
        }
        return () => {
            for (let i = 0; i < changedElems.length; i++) {
                const changedElem = changedElems[i];
                changedElem.classList.remove("selected");
            }
        };
    }, [activeIDs, editor, markNodeMap]);

    /**
     * Handle scroll to selected comment. Selected from text editor
     */
    useEffect(() => {
        if (!activeCommentClassName || !rightPanelRef) return;
        // Scroll to element when it's created in DOM
        const interval = setInterval(() => {
            const relatedCommentEl = rightPanelRef?.current?.querySelector(activeCommentClassName);
            if (relatedCommentEl) {
                relatedCommentEl.scrollIntoView({ behavior: "smooth", block: "start" });
                clearInterval(interval);
            }
        }, 50);

        return () => clearInterval(interval);
    }, [activeCommentClassName]);

    useEffect(() => {
        const markNodeKeysToIDs: Map<NodeKey, Array<string>> = new Map();

        return mergeRegister(
            registerNestedElementResolver<MarkNode>(
                editor,
                MarkNode,
                (from: MarkNode) => {
                    return $createMarkNode(from.getIDs());
                },
                (from: MarkNode, to: MarkNode) => {
                    // Merge the IDs
                    const ids = from.getIDs();
                    ids.forEach((id) => {
                        to.addID(id);
                    });
                },
            ),
            editor.registerMutationListener(MarkNode, (mutations) => {
                editor.getEditorState().read(() => {
                    for (const [key, mutation] of mutations) {
                        const node: null | MarkNode = $getNodeByKey(key);
                        let ids: NodeKey[] = [];

                        if (mutation === "destroyed") {
                            ids = markNodeKeysToIDs.get(key) || [];
                        } else if ($isMarkNode(node)) {
                            ids = node.getIDs();
                        }

                        for (let i = 0; i < ids.length; i++) {
                            const id = ids[i];
                            let markNodeKeys = markNodeMap.get(id);
                            markNodeKeysToIDs.set(key, ids);

                            if (mutation === "destroyed") {
                                if (markNodeKeys !== undefined) {
                                    markNodeKeys.delete(key);
                                    if (markNodeKeys.size === 0) {
                                        markNodeMap.delete(id);
                                    }
                                }
                            } else {
                                if (markNodeKeys === undefined) {
                                    markNodeKeys = new Set();
                                    markNodeMap.set(id, markNodeKeys);
                                }
                                if (!markNodeKeys.has(key)) {
                                    markNodeKeys.add(key);
                                }
                            }
                        }
                    }
                });
            }),
            editor.registerUpdateListener(({ editorState, tags }) => {
                editorState.read(() => {
                    const selection = $getSelection();
                    let hasActiveIds = false;
                    let hasAnchorKey = false;

                    if ($isRangeSelection(selection)) {
                        const anchorNode = selection.anchor.getNode();
                        if ($isTextNode(anchorNode)) {
                            const commentIDs = $getMarkIDs(anchorNode, selection.anchor.offset);
                            if (commentIDs !== null) {
                                setActiveIDs(commentIDs);
                                setActiveCommentClassName(`.selected-comment-${commentIDs[0]}`);
                                hasActiveIds = true;
                            }
                            if (!selection.isCollapsed()) {
                                setActiveAnchorKey(anchorNode.getKey());
                                hasAnchorKey = true;
                            }
                        }
                    }
                    if (!hasActiveIds) {
                        if ($isRangeSelection(selection)) {
                            const anchorNode = selection.anchor.getNode();
                            $isParagraphNode(anchorNode) &&
                                setActiveIDs((_activeIds) => (_activeIds.length === 0 ? _activeIds : []));
                        }
                    }
                    if (!hasAnchorKey) {
                        setActiveAnchorKey(null);
                    }
                    if (!tags.has("collaboration") && $isRangeSelection(selection)) {
                        setShowCommentInput(false);
                    }
                });
            }),
            editor.registerCommand(
                INSERT_INLINE_COMMAND,
                () => {
                    const domSelection = window.getSelection();
                    if (domSelection !== null) {
                        domSelection.removeAllRanges();
                    }
                    setShowCommentInput(true);
                    return true;
                },
                COMMAND_PRIORITY_EDITOR,
            ),
        );
    }, [editor, markNodeMap]);

    const onAddComment = () => {
        editor.dispatchCommand(INSERT_INLINE_COMMAND, undefined);
    };

    return (
        <>
            {showCommentInput &&
                createPortal(
                    <CommentInputBox
                        editor={editor}
                        cancelAddComment={cancelAddComment}
                        submitAddComment={submitAddComment}
                    />,
                    document.body,
                )}
            {commentListElement &&
                createPortal(
                    <CommentsPanel
                        comments={comments}
                        submitAddComment={submitAddComment}
                        deleteCommentOrThread={deleteCommentOrThread}
                        activeIDs={activeIDs}
                        markNodeMap={markNodeMap}
                    />,
                    commentListElement,
                )}
        </>
    );
}
